import { FuelToReconcileTableComponent } from './components/tables/fuel-to-reconcile-table/fuel-to-reconcile-table.component';
import { NgIf } from '@angular/common';
import { ReconciledFuelSlipsTableComponent } from './components/tables/reconciled-fuel-slips-table/reconciled-fuel-slips-table.component';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { UploadPreferenceModalComponent } from './components/modals/upload-preference-modal/upload-preference-modal.component';
import { FuelReconciliationService } from './services/fuel-reconciliation.service';
import { Subject } from 'rxjs';
import { DateIntervalFilterData, User, WidgetFilterData } from '../../_shared/models';
import { AuthService, FilterService } from '../../_shared/services';
import { readExcelFile } from '../_shared/helpers/excel-reader.helper';
import { convertRowToFuelSlipImport } from './helpers/fuel-slip-upload.helper';
import { FuelSlipUploadPreference } from './models/fuel-recon.model';
import { ToastrService } from 'ngx-toastr';
import { FuelReconKpiWidgetsComponent } from './components/widgets/fuel-recon-kpi-widgets/fuel-recon-kpi-widgets.component';
import { toSeconds } from './helpers/timezone.helper';
import { Component, ElementRef, inject, ViewChild } from '@angular/core';
import { SearchComponent } from '../../_shared/components/filters/search/search.component';
import { DateFilterComponent } from '../../_shared/components';
import { DateInterval } from '../../_shared/enums';
import { isEmptyObject } from '../../_shared/helpers/object.helper';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-fuel-reconciliation',
  standalone: true,
  imports: [
    FuelToReconcileTableComponent,
    ReconciledFuelSlipsTableComponent,
    NgIf,
    FuelReconKpiWidgetsComponent,
    DateFilterComponent,
    SearchComponent,
    TranslocoDirective
  ],
  templateUrl: './fuel-reconciliation.component.html',
  styleUrl: './fuel-reconciliation.component.scss',
})
export class FuelReconciliationComponent {
  @ViewChild('fileInput', { static: false }) fileInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild(FuelToReconcileTableComponent) fuelToReconcileTable!: FuelToReconcileTableComponent;
  @ViewChild(ReconciledFuelSlipsTableComponent) reconciledFuelSlipsTable!: ReconciledFuelSlipsTableComponent;
  public isUploading = false;
  public activeTab: string | 'fuelToReconcile' | 'reconciledSlips' = 'fuelToReconcile';
  public dateFilter: DateIntervalFilterData;
  public defaultInterval: DateInterval = DateInterval.Month;
  public disabledIntervals: DateInterval[] = [DateInterval.Half];
  public isLoading: boolean = false;
  private transloco = inject(TranslocoService);
  private User: User;
  private ignoreNextFileInputClick = false;
  protected cancelSubject = new Subject<void>();

  constructor(
    private modalService: NgbModal,
    private fuelReconService: FuelReconciliationService,
    private authService: AuthService,
    private toastr: ToastrService,
    private filterService: FilterService
  ) {
    this.User = this.authService.getUser();
    this.dateFilter = {} as DateIntervalFilterData;
  }

  public async onSlipReconciled(): Promise<void> {
    await this.reconciledFuelSlipsTable?.getPaginatedReconciledFuelByAccountIdAsync();
  }

  public async onSlipDeleted(): Promise<void> {
    await this.fuelToReconcileTable?.getPaginatedFuelToReconcileByAccountIdAsync();
  }

  public async toggleTab(tab: 'fuelToReconcile' | 'reconciledSlips'): Promise<void> {
    if (this.activeTab !== tab) {
      this.activeTab = tab;
    }
  }

  public onDateFilterChange(
    filter: DateIntervalFilterData
  ): void {
    this.dateFilter = filter;
    if (!isEmptyObject(this.dateFilter)) {
      this.filterService.updateFilter(this.dateFilter as WidgetFilterData)
    }
  }

  public onSearchTermUpdate(searchTerm: string): void {
    this.filterService.updateSearchTerm(searchTerm);
  }

  openUploadPreferenceModal(fromUploadExcel: boolean = false): void {
    if (this.activeTab === 'reconciledSlips') {
      return;
    }
    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static',
    };
    const modalRef: NgbModalRef = this.modalService.open(UploadPreferenceModalComponent, modalOptions);
    const modalComponent = modalRef.componentInstance as UploadPreferenceModalComponent;
    if (fromUploadExcel && modalComponent.upsertSuccess) {
      modalComponent.upsertSuccess.subscribe(() => {
        this.fileInputRef.nativeElement.click();
      });
    }
  }

  async onFileSelected(event: any): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (!(input.files && input.files.length > 0)) {
      return;
    }
    const file = input.files[0];
    const result = await readExcelFile(file);

    const fuelSlipUploadPreference = (await this.fuelReconService.getFuelSlipUploadPreferenceAsync(this.cancelSubject)).data as FuelSlipUploadPreference;
    const fuelSlips = convertRowToFuelSlipImport(result.slice(fuelSlipUploadPreference.startRow), this.User.login, new Date(), fuelSlipUploadPreference);
    input.value = '';
    this.isUploading = true;
    let offset = (await this.fuelReconService.getFuelSlipUploadPreferenceAsync(this.cancelSubject))?.data?.utcOffset;
    if (offset === undefined || offset === null) {
      offset = -toSeconds(new Date().getTimezoneOffset());
    }

    await this.fuelReconService.bulkReconcileFuelSlipsAsync({
      fuelSlips,
      utcOffset: offset
    }, this.cancelSubject)
      .then(async () => {
        this.toastr.success(this.transloco.translate('fuelReconciliation.messages.bulkReconcileSuccess'));
        await this.fuelToReconcileTable?.getPaginatedFuelToReconcileByAccountIdAsync();
        await this.reconciledFuelSlipsTable?.getPaginatedReconciledFuelByAccountIdAsync();
      })
      .catch(() => this.toastr.error(this.transloco.translate('fuelReconciliation.messages.bulkReconcileError')))
      .finally(() => this.isUploading = false);
  }

  async onFileInputClick(event: MouseEvent): Promise<void> {
    if (this.ignoreNextFileInputClick) {
      this.ignoreNextFileInputClick = false;
      return;
    }
    try {
      event.preventDefault();
      const pref = await this.fuelReconService.getFuelSlipUploadPreferenceAsync(this.cancelSubject);
      if (!pref.data) {
        return;
      }
      const needsCurrency = pref.data.currencyId === null;
      const needsAmountType = pref.data.amountTypeColumn === null;

      if (needsCurrency || needsAmountType) {
        let msg = this.transloco.translate('fuelReconciliation.messages.setPreferencesToContinue');
        if (needsCurrency && needsAmountType) msg = this.transloco.translate('fuelReconciliation.messages.setCurrencyAndCostType');
        else if (needsCurrency) {
          msg = this.transloco.translate('fuelReconciliation.messages.setCurrencyColumn');
        }
        else if (needsAmountType) {
          msg = this.transloco.translate('fuelReconciliation.messages.setCostTypeColumn');
        }

        this.toastr.warning(msg, this.transloco.translate('fuelReconciliation.messages.uploadPreferencesRequired'));
        this.openUploadPreferenceModal(true);
        return;
      }

      if (this.fileInputRef?.nativeElement) {
        this.fileInputRef.nativeElement.value = '';
        setTimeout(() => {
          this.ignoreNextFileInputClick = true;
          this.fileInputRef.nativeElement.click();
        });
      } else {
        this.toastr.error(this.transloco.translate('fuelReconciliation.messages.fileInputUnavailable'), this.transloco.translate('fuelReconciliation.messages.error'));
      }
    } catch (error) {
      this.toastr.warning(this.transloco.translate('fuelReconciliation.messages.setUpUploadPreferenceFirst'), this.transloco.translate('fuelReconciliation.messages.uploadPreferenceRequired'));
      this.openUploadPreferenceModal(true);
    }
  }
}